import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AlertCircle,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  Clock3,
  FileText,
  History,
  Info,
  Loader2,
  Plus,
  Send,
  TimerReset,
  Upload,
  XCircle,
  CheckSquare
} from "lucide-react";

import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import { MIN_TEXT_WORDS, getMinimumWordsError } from "../../utils/textValidation";

const statusStyle = {
  Pending: "bg-amber-500/10 text-amber-600",
  Accepted: "bg-blue-500/10 text-blue-600",
  Rejected: "bg-red-500/10 text-red-600",
  "In Clearance": "bg-violet-500/10 text-violet-600",
  Completed: "bg-emerald-500/10 text-emerald-600",
};

const checklistLabels = {
  laptopReturned: "Asset return: laptop/system",
  idCardReturned: "Asset return: ID card",
  pendingSalaryCleared: "Finance: pending salary cleared",
  expensesSettled: "Finance: expenses settled",
  companyAccessRemoved: "Access: company accounts removed",
  teamLeadApproved: "Manager: handover approved",
  hrApproved: "HR: exit interview/final approval",
  finalSettlementCompleted: "Final settlement completed",
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const getOffboardingCacheKey = (employee) => `employee_offboarding_${employee?._id || employee?.id || "self"}`;

const readOffboardingCache = (employee) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(getOffboardingCacheKey(employee)) || "{}");
    return Array.isArray(parsed.data) ? parsed.data : [];
  } catch {
    return [];
  }
};

const writeOffboardingCache = (employee, records) => {
  try {
    localStorage.setItem(getOffboardingCacheKey(employee), JSON.stringify({ data: records, savedAt: Date.now() }));
  } catch {
    // Live data can still load if local cache is unavailable.
  }
};

const OffboardingMetric = ({ icon, label, value, subLabel, active = false, tone = "success" }) => (
  <div className={`leaves-kpi-glass ${active ? "active" : ""} ${tone}`}>
    <span className="leave-metric-icon">{icon}</span>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
      {subLabel && <small>{subLabel}</small>}
    </div>
  </div>
);

const SummaryLine = ({ label, value, muted = false }) => (
  <div className={`leave-summary-line ${muted ? "muted" : ""}`}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const DocumentLink = ({ document, label }) => {
  if (!document?.data) return null;
  return (
    <a
      href={document.data}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-xs font-black text-[var(--text-primary)] hover:border-[#10b981] hover:text-[#10b981] transition-colors"
    >
      <FileText size={14} />
      {label || document.name || "Open"}
    </a>
  );
};

const HistoryRow = ({ record, open, onClick }) => {
  const clearanceEntries = Object.entries(checklistLabels);
  const completed = clearanceEntries.filter(([key]) => record.clearance?.[key]).length;
  const isApproved = record.status === "Accepted" || record.status === "In Clearance" || record.status === "Completed";
  const StatusIcon = isApproved ? CheckCircle2 : record.status === "Rejected" ? XCircle : Clock3;

  return (
    <button type="button" className="leaves-history-item !text-left" onClick={onClick}>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3">
          <span className={`flex items-center justify-center w-10 h-10 rounded-full shrink-0 ${isApproved ? "bg-emerald-500/10 text-emerald-600" : record.status === "Rejected" ? "bg-red-500/10 text-red-500" : "bg-amber-500/10 text-amber-600"}`}>
            <StatusIcon size={18} />
          </span>
          <div>
            <strong className="block text-sm text-[var(--text-primary)]">Resignation Request</strong>
            <span className="text-xs text-[var(--text-secondary)]">LWD: {formatDate(record.approvedLastWorkingDate || record.requestedLastWorkingDate)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <small className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${isApproved ? "bg-emerald-500/10 text-emerald-600" : record.status === "Rejected" ? "bg-red-500/10 text-red-500" : "bg-amber-500/10 text-amber-600"}`}>
            {record.status}
          </small>
          <span className="text-[10px] text-[var(--text-secondary)] font-bold">{record.noticeDays || 0} notice days</span>
        </div>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
          <p className="text-sm text-[var(--text-primary)] mb-3">{record.reason}</p>
          
          <div className="flex flex-wrap gap-2 mb-4">
            <DocumentLink document={record.resignationLetter} label="Resignation Letter" />
            <DocumentLink document={record.experienceLetter} label="Experience Letter" />
            <DocumentLink document={record.relievingLetter} label="Relieving Letter" />
          </div>

          {record.adminNote && (
            <em className="block mb-4 text-xs font-bold text-blue-500 not-italic bg-blue-500/10 px-3 py-2 rounded-lg flex items-center gap-2">
              <Info size={14} /> Note: {record.adminNote}
            </em>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div className="bg-[var(--bg-primary)] p-3 rounded-xl border border-[var(--border-color)]">
              <p className="text-xs font-bold flex justify-between items-center text-[var(--text-primary)] mb-2 border-b border-[var(--border-color)] pb-1">
                <span>Clearance</span>
                <span className="text-[#10b981]">{completed}/{clearanceEntries.length}</span>
              </p>
              <div className="space-y-1.5">
                {clearanceEntries.map(([key, label]) => (
                  <div key={key} className={`flex items-start gap-2 text-[11px] font-bold ${record.clearance?.[key] ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)] opacity-70'}`}>
                    <CheckCircle2 size={13} className={`mt-0.5 shrink-0 ${record.clearance?.[key] ? "text-[#10b981]" : "text-[var(--border-color)]"}`} />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[var(--bg-primary)] p-3 rounded-xl border border-[var(--border-color)]">
              <p className="text-xs font-bold text-[var(--text-primary)] mb-2 border-b border-[var(--border-color)] pb-1">Timeline</p>
              <div className="space-y-2.5">
                {(record.timeline || []).map((item, index) => (
                  <div key={index} className="flex gap-2 relative">
                    {index !== record.timeline.length - 1 && <span className="absolute left-1 top-4 w-px h-full bg-[var(--border-color)] z-0"></span>}
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#10b981] z-10" />
                    <div>
                      <p className="text-[11px] font-bold text-[var(--text-primary)]">{item.label}</p>
                      <p className="text-[10px] font-medium text-[var(--text-secondary)]">{formatDateTime(item.at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </button>
  );
};

const EmployeeOffboarding = () => {
  const { employee } = useAuth();
  const location = useLocation();
  const canMonitorOffboarding = employee?.role === "lead"
    || String(employee?.role || "").toLowerCase() === "hr"
    || String(employee?.team || "").toLowerCase() === "hr";
  const cachedRecords = useMemo(() => readOffboardingCache(employee), [employee]);
  
  const [records, setRecords] = useState(cachedRecords);
  const [teamRecords, setTeamRecords] = useState([]);
  const [applyOpen, setApplyOpen] = useState(false);
  const [openRecordId, setOpenRecordId] = useState("");
  
  const [requestedLastWorkingDate, setRequestedLastWorkingDate] = useState("");
  const [reason, setReason] = useState("");
  const [letter, setLetter] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitIndicator, setSubmitIndicator] = useState("");
  const [message, setMessage] = useState("");

  const fetchRecords = useCallback(async () => {
    try {
      const res = await api.get("/api/employee/offboarding", { params: { limit: 30 }, cache: false });
      const next = Array.isArray(res.data) ? res.data : [];
      setRecords(next);
      writeOffboardingCache(employee, next);
    } catch (error) {
      console.error("Failed to load offboarding:", error);
    }
  }, [employee]);

  const fetchTeamRecords = useCallback(async () => {
    if (!canMonitorOffboarding) return;
    try {
      const res = await api.get("/api/employee/team/offboarding", { params: { limit: 80 }, cache: false });
      setTeamRecords(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to load team offboarding:", error);
    }
  }, [canMonitorOffboarding]);

  useEffect(() => {
    fetchRecords();
    fetchTeamRecords();
  }, [fetchRecords, fetchTeamRecords]);

  useEffect(() => {
    if (!canMonitorOffboarding || !location.search.includes("view=employee-list")) return;
    const timer = window.setTimeout(() => {
      document.getElementById("employee-offboarding-list")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [canMonitorOffboarding, location.search]);

  const activeRequest = records.find((record) => !["Rejected", "Completed"].includes(record.status));
  const completedClearanceTasks = activeRequest ? Object.values(activeRequest.clearance || {}).filter(Boolean).length : 0;
  const totalClearanceTasks = Object.keys(checklistLabels).length;

  const closeApplyModal = () => {
    if (submitting) return;
    setApplyOpen(false);
    setMessage("");
  };

  const submitRequest = async (event) => {
    event.preventDefault();
    setMessage("");
    if (!requestedLastWorkingDate || !reason.trim()) return;

    const reasonError = getMinimumWordsError(reason, "Resignation reason");
    if (reasonError) {
      setMessage(reasonError);
      return;
    }

    setSubmitting(true);
    setSubmitIndicator("submitting");
    try {
      const letterData = letter ? await fileToDataUrl(letter) : "";
      const res = await api.post("/api/employee/offboarding", {
        requestedLastWorkingDate,
        reason,
        resignationLetterName: letter?.name || "",
        resignationLetter: letterData,
      }, { skipSaveToast: true });

      const next = [res.data, ...records];
      setRecords(next);
      writeOffboardingCache(employee, next);
      setRequestedLastWorkingDate("");
      setReason("");
      setLetter(null);
      setSubmitIndicator("submitted");
      fetchTeamRecords();
      window.setTimeout(() => {
        setSubmitIndicator("");
        setApplyOpen(false);
      }, 1500);
    } catch (error) {
      setSubmitIndicator("failed");
      setMessage(error.response?.data?.message || "Could not submit resignation.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="leaves-premium-wrapper text-[var(--text-primary)]">
      <main className="leave-reference-shell">

        {submitIndicator && (
          <div className={`mb-6 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-bold shadow-sm ${
            submitIndicator === "failed"
              ? "border-red-500/20 bg-red-500/10 text-red-600"
              : "border-emerald-500/20 bg-emerald-500/10 text-emerald-600"
          }`}>
            {submitIndicator === "submitting" ? (
              <Loader2 size={17} className="animate-spin" />
            ) : submitIndicator === "failed" ? (
              <AlertCircle size={17} />
            ) : (
              <CheckCircle2 size={17} />
            )}
            <span>
              {submitIndicator === "submitting"
                ? "Submitting resignation request..."
                : submitIndicator === "failed"
                  ? "Submission failed"
                  : "Resignation request submitted successfully"}
            </span>
          </div>
        )}

        <div className="leave-reference-layout">
          <div className="leave-reference-main">
            <section className="leaves-premium-hero">
              <div className="leaves-hero-top">
                <div className="leaves-hero-content">
                  <span className="premium-eyebrow text-[#10b981]">Offboarding</span>
                  <h1>Exit Request</h1>
                  <p>Submit your resignation request and track approval, LWD, clearance, and documents.</p>
                  <div className="premium-meta-row mt-4">
                    <span><CalendarDays size={14} /> {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "short", year: "numeric" })}</span>
                  </div>
                </div>

                <div className="leave-days-ring">
                  <strong>{activeRequest ? activeRequest.noticeDays || 0 : 0}</strong>
                  <span>Notice</span>
                  <small>Days</small>
                </div>
              </div>

              <div className="leaves-kpi-premium-grid">
                <OffboardingMetric icon={<Clock3 size={19} />} label="Status" value={activeRequest?.status || "None"} subLabel="Current request" tone={activeRequest ? "warning" : "info"} active={!!activeRequest} />
                <OffboardingMetric icon={<CalendarDays size={19} />} label="LWD" value={activeRequest ? formatDate(activeRequest.approvedLastWorkingDate || activeRequest.requestedLastWorkingDate) : "-"} subLabel="Last working date" />
                <OffboardingMetric icon={<CheckSquare size={19} />} label="Clearance" value={`${completedClearanceTasks}/${totalClearanceTasks}`} subLabel="Tasks done" />
              </div>
            </section>

            <section className="leaves-premium-board leave-apply-card">
              <div className="leaves-board-header">
                <div>
                  <h2>Resignation Request</h2>
                  <p>Submit proposed LWD, reason, and letter</p>
                </div>
                {!activeRequest && (
                  <button className="leaves-new-btn" type="button" onClick={() => setApplyOpen(true)}>
                    <Plus size={18} />
                    New Request
                  </button>
                )}
              </div>

              {activeRequest ? (
                <div className="leave-apply-callout border border-amber-500/20 bg-amber-500/10">
                  <div className="leave-callout-icon text-amber-600 bg-amber-500/20">
                    <History size={24} />
                  </div>
                  <div>
                    <strong className="text-amber-700 dark:text-amber-400">Request in Progress</strong>
                    <span>Current status: <strong className="uppercase">{activeRequest.status}</strong>. Track LWD, clearance, and documents below.</span>
                  </div>
                </div>
              ) : (
                <div className="leave-apply-callout">
                  <div className="leave-callout-icon">
                    <BriefcaseBusiness size={24} />
                  </div>
                  <div>
                    <strong>No active request</strong>
                    <span>Submit your proposed last working date and reason to start offboarding.</span>
                  </div>
                  <button type="button" onClick={() => setApplyOpen(true)}>
                    Submit Request
                  </button>
                </div>
              )}
            </section>

            <section className="leaves-premium-board leave-history-card">
              <div className="leaves-board-header">
                <div>
                  <h2>Request History</h2>
                  <p>Submitted resignation requests and status</p>
                </div>
                <button type="button" onClick={fetchRecords} className="text-xs font-bold text-[#10b981] hover:underline flex items-center gap-1">
                  <TimerReset size={14} /> Refresh
                </button>
              </div>

              <div className="flex flex-col gap-3">
                {records.length === 0 ? (
                  <div className="leave-empty-reference">
                    <BriefcaseBusiness size={76} className="text-[var(--border-color)]" />
                    <strong className="block text-[var(--text-primary)]">No requests yet</strong>
                    <span className="text-sm text-[var(--text-secondary)]">Submit a resignation request to begin offboarding.</span>
                  </div>
                ) : (
                  records.map((record) => (
                    <HistoryRow
                      key={record._id}
                      record={record}
                      open={openRecordId === record._id}
                      onClick={() => setOpenRecordId((current) => (current === record._id ? "" : record._id))}
                    />
                  ))
                )}
              </div>
            </section>
          </div>

          <aside className="leave-reference-side">
            <section className="leaves-premium-board leave-side-card">
              <div className="leave-side-title">
                <span><CheckSquare size={18} /></span>
                <h2>Current Request</h2>
              </div>

              <div className="leave-summary-grid">
                <div className="leaves-donut-inner border-4 border-[#10b981] !text-[#10b981]">
                  <strong>{completedClearanceTasks}</strong>
                  <span>Clearance</span>
                </div>
                <div className="leave-summary-list">
                  <SummaryLine label="Status" value={activeRequest?.status || "N/A"} />
                  <SummaryLine label="LWD" value={activeRequest ? formatDate(activeRequest.approvedLastWorkingDate || activeRequest.requestedLastWorkingDate) : "-"} muted />
                  <SummaryLine label="Pending Clearance" value={`${totalClearanceTasks - completedClearanceTasks} items`} />
                </div>
              </div>
            </section>

            {canMonitorOffboarding && (
              <section id="employee-offboarding-list" className="leaves-premium-board leave-side-card">
                <div className="leave-side-title flex justify-between items-center w-full">
                  <div className="flex items-center gap-2">
                    <span className="text-blue-500"><BriefcaseBusiness size={18} /></span>
                    <h2>Team Offboarding</h2>
                  </div>
                  <button type="button" onClick={fetchTeamRecords} className="p-1 hover:bg-[var(--bg-secondary)] rounded-md text-[var(--text-secondary)]">
                    <TimerReset size={14} />
                  </button>
                </div>
                <div className="flex flex-col gap-3 mt-4">
                  {!teamRecords.length ? (
                    <p className="text-xs text-[var(--text-secondary)] font-medium text-center p-4 border border-dashed border-[var(--border-color)] rounded-xl">
                      No active team offboarding requests.
                    </p>
                  ) : (
                    teamRecords.map((record) => (
                      <div key={record._id} className="p-3 border border-[var(--border-color)] rounded-xl bg-[var(--bg-primary)] hover:border-[#10b981] transition-colors">
                        <div className="flex justify-between items-start mb-2">
                          <strong className="text-sm font-bold block">{record.employee?.name || "Unknown"}</strong>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${statusStyle[record.status] || statusStyle.Pending}`}>
                            {record.status}
                          </span>
                        </div>
                        <div className="text-xs text-[var(--text-secondary)] flex justify-between">
                          <span>LWD: <strong className="text-[var(--text-primary)]">{formatDate(record.approvedLastWorkingDate || record.requestedLastWorkingDate)}</strong></span>
                          <span className="bg-[var(--bg-secondary)] px-1.5 rounded">{record.employee?.employeeId}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>
            )}
            
            <section className="leaves-premium-board leave-side-card">
              <div className="leave-side-title">
                <span className="text-[#10b981]"><Info size={18} /></span>
                <h2>Required Steps</h2>
              </div>
              <div className="space-y-4 mt-4 text-sm text-[var(--text-secondary)]">
                 <p><strong>Submit:</strong> Proposed last working date, reason, and resignation letter if available.</p>
                 <p><strong>Clearance:</strong> Complete asset return, handover, access removal, and finance checks.</p>
                 <p><strong>Documents:</strong> Experience and relieving letters appear here after HR completion.</p>
              </div>
            </section>
          </aside>
        </div>
      </main>

      {applyOpen && (
        <div className="leave-v3-modal-backdrop" onMouseDown={closeApplyModal}>
          <section className="leave-v3-modal" onMouseDown={(event) => event.stopPropagation()}>
            <header className="leave-v3-modal-head">
              <div>
                <span className="premium-eyebrow text-[#10b981]">Offboarding</span>
                <h2>Resignation Form</h2>
              </div>
              <button type="button" onClick={closeApplyModal} aria-label="Close form">
                <XCircle size={20} />
              </button>
            </header>

            <form className="leave-v3-form" onSubmit={submitRequest}>
              <div className="leave-v3-form-grid">
                <label className="leave-v3-input">
                  <span>Proposed Last Working Date</span>
                  <input
                    type="date"
                    required
                    value={requestedLastWorkingDate}
                    onChange={(event) => setRequestedLastWorkingDate(event.target.value)}
                  />
                </label>

                <label className="leave-v3-input">
                  <span>Resignation Letter (Optional)</span>
                  <div className="relative flex w-full items-center">
                    <input
                      type="file"
                      id="resignation-letter"
                      className="hidden"
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(event) => setLetter(event.target.files?.[0] || null)}
                    />
                    <label htmlFor="resignation-letter" className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-bold text-[var(--text-secondary)] hover:border-[#10b981] hover:text-[var(--text-primary)] transition-colors">
                      <span className="truncate">{letter?.name ? letter.name : "Choose a file..."}</span>
                      <Upload size={16} className="text-[#10b981] shrink-0" />
                    </label>
                  </div>
                </label>
              </div>

              <label className="leave-v3-input">
                <span>Reason for Leaving</span>
                <textarea
                  required
                  rows={4}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={`Write a clear reason for the request in at least ${MIN_TEXT_WORDS} words...`}
                />
              </label>

              {message && (
                <div className="leave-v3-alert bg-red-500/10 text-red-500 border-red-500/20">
                  <AlertCircle size={17} />
                  {message}
                </div>
              )}

              <div className="leave-v3-submit">
                <span>
                  This request will be sent to HR/admin for review.
                </span>
                <div className="leave-v3-submit-actions">
                  <button type="button" onClick={closeApplyModal} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting || !requestedLastWorkingDate || !reason.trim()}>
                    {submitting ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                    {submitting ? "Submitting" : "Submit"}
                  </button>
                </div>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
};

export default EmployeeOffboarding;
